import React, { useEffect, useRef } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { useEnteredView } from '@fiverr-private/hooks';
import ProSection from '../ProSection';
import { sendBiankaImpression, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { addTransferToCarouselAnimation, addFadeOutAnimations, addScrollSpeedMotion } from '../../shared/animations';
import SellerCell from './SellerCell';
import { sellers } from './constants';

import * as styles from './styles.ve.css';

const SellersGallery: React.FC<{ isV16?: boolean }> = ({ isV16 = false }) => {
  const main = useRef<HTMLDivElement>(null);
  const eventTrackingRef = useRef<HTMLDivElement>(null);

  useEnteredView(eventTrackingRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.TOP_MOTION_IS_SHOWN,
        componentName: 'top_motion',
        componentText: 'SellersGallery',
        isV16,
      });

      sendMixpanelEvent({
        eventName: EVENT_NAMES.MIXPANEL.LOHP_BRAND_MOTION_IS_TRIGGERED,
        isV16,
      });
    },
  });

  useEffect(() => {
    const cleanupFadeOutAnimations = isV16 ? undefined : addFadeOutAnimations();
    const cleanupTransferToCarouselAnimation = isV16 ? undefined : addTransferToCarouselAnimation();
    const cleanupScrollSpeedMotion = isV16 ? undefined : addScrollSpeedMotion(main.current as HTMLElement);

    return () => {
      cleanupFadeOutAnimations?.();
      cleanupTransferToCarouselAnimation?.();
      cleanupScrollSpeedMotion?.();
    };
  }, [isV16]);

  // TODO: Decompose this component into smaller components like SellersGalleryColumn and SellersGalleryRow
  return (
    <Container data-scroll-wrapper width="100%">
      {!isV16 && (
        <Container
          width="100%"
          ref={main}
          position="relative"
          zIndex="2"
          className={styles.sellersGalleryContainerWrapper}
        >
          <Container
            position="relative"
            width="100%"
            overflowX={{ default: 'hidden', lg: 'clip' }}
            gap={{ default: '4', sm: '5', md: '4', lg: '4', xl: '5' }}
            className={styles.sellersGalleryContainer}
            data-scroll-content
          >
            <Stack
              position="relative"
              width={{
                default: 'calc(100% + 67px + 67px)',
                sm: 'calc(100% + 38px + 38px)',
                md: 'calc(100% + 44px + 44px)',
                lg: 'calc(100% + 44px + 142px)',
                xl: 'calc(100% + 76px + 197px)',
              }}
              className={styles.sellersGallery}
            >
              <Stack
                direction="column"
                minWidth={{ default: '154px', sm: '212px', md: '232px', lg: '252px', xl: '312px' }}
                width={{ default: '31.17%', sm: '31.36%', md: '23.48%', lg: '18.72%', xl: '18.65%' }}
                maxWidth={{ default: '212px', sm: '232px', md: '252px', lg: '312px', xl: '436px' }}
                className={styles.sellersGalleryCol1}
                data-speed="2"
              >
                {sellers['1-col'].map((seller) => (
                  <SellerCell key={seller.name} {...seller} />
                ))}
              </Stack>
              <Stack
                direction="column"
                minWidth={{ default: '154px', sm: '212px', md: '232px', lg: '516px', xl: '644px' }}
                width={{ default: '31.17%', sm: '31.36%', md: '23.48%', lg: '38.33%', xl: '38.5%' }}
                maxWidth={{ default: '212px', sm: '232px', md: '252px', lg: '644px', xl: '902px' }}
                className={styles.sellersGalleryCol2}
              >
                <Stack
                  marginTop={{ xl: '4' }}
                  className={styles.sellersGalleryCol2Row1}
                  direction="row"
                  ref={eventTrackingRef}
                >
                  <SellerCell {...sellers['2-col']['1-row']['1-col'][0]} />
                  <Stack
                    direction="column"
                    hidden={{
                      default: true,
                      lg: false,
                    }}
                    flexGrow={1}
                    minWidth={{ lg: '304px', xl: '380px' }}
                    width={{ lg: '58.91%', xl: '58.98%' }}
                    maxWidth={{ lg: '380px', xl: '532px' }}
                    className={styles.sellersGalleryCol2Row1Col2}
                  >
                    {sellers['2-col']['1-row']['2-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                </Stack>
                <Stack className={styles.sellersGalleryCol2Row2} direction="row" data-speed="1.25" width="100%">
                  <Stack
                    direction="column"
                    minWidth={{ lg: '252px', xl: '314px' }}
                    width={{ default: '100%', lg: '48.84%', xl: '48.78%' }}
                    maxWidth={{ lg: '314px', xl: '440px' }}
                    className={styles.sellersGalleryCol2Row2Col1}
                  >
                    {sellers['2-col']['2-row']['1-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                  <Stack
                    direction="column"
                    hidden={{
                      default: true,
                      lg: false,
                    }}
                    minWidth={{ lg: '248px', xl: '310px' }}
                    width={{ lg: '48.06%', xl: '48.14%' }}
                    maxWidth={{ lg: '310px', xl: '432px' }}
                    className={styles.sellersGalleryCol2Row2Col2}
                    data-speed="1.75"
                  >
                    {sellers['2-col']['2-row']['2-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                direction="column"
                minWidth={{ default: '154px', sm: '212px', md: '484px', lg: '546px', xl: '677px' }}
                width={{ default: '31.17%', sm: '31.36%', md: '47%', lg: '40.57%', xl: '40.47%' }}
                maxWidth={{ default: '212px', sm: '232px', md: '524px', lg: '677px', xl: '946px' }}
                className={styles.sellersGalleryCol3}
              >
                <Stack className={styles.sellersGalleryCol3Row1} direction="row" width="100%">
                  <Stack
                    direction="column"
                    hidden={{
                      default: true,
                      lg: false,
                    }}
                    minWidth={{ lg: '202px', xl: '249px' }}
                    width={{ lg: '37%', xl: '36.78%' }}
                    maxWidth={{ lg: '249px', xl: '348px' }}
                    flexShrink={{ lg: 0 }}
                    className={styles.sellersGalleryCol3Row1Col1}
                  >
                    {sellers['3-col']['1-row']['1-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                  <Stack
                    direction="column"
                    minWidth={{ sm: '212px', md: '232px', lg: '250px', xl: '310px' }}
                    width={{ default: '100%', sm: '50%', md: '50%', lg: '45.79%', xl: '45.79%' }}
                    maxWidth={{ sm: '232px', md: '252px', lg: '310px', xl: '432px' }}
                    flexShrink={{ sm: 0 }}
                    className={styles.sellersGalleryCol3Row1Col2}
                  >
                    {sellers['3-col']['1-row']['2-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                  <Stack
                    direction="column"
                    flexShrink={0}
                    hidden={{
                      default: true,
                      sm: false,
                    }}
                    minWidth={{ sm: '212px', md: '232px', lg: '52px', xl: '78px' }}
                    width={{ sm: '50%', md: '50%', lg: '9.52%', xl: '11.52%' }}
                    maxWidth={{ sm: '232px', md: '252px', lg: '78px', xl: '111px' }}
                    className={styles.sellersGalleryCol3Row1Col3}
                  >
                    {sellers['3-col']['1-row']['3-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  hidden={{
                    default: true,
                    lg: false,
                  }}
                  width="100%"
                >
                  <Stack
                    direction="column"
                    data-speed="1.75"
                    minWidth={{ lg: '278px', xl: '344px' }}
                    width={{ lg: '50.92%', xl: '50.81%' }}
                    maxWidth={{ lg: '344px', xl: '440px' }}
                    className={styles.sellersGalleryCol3Row2Col1}
                  >
                    {sellers['3-col']['2-row']['1-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                  <Stack
                    direction="column"
                    minWidth={{ lg: '252px', xl: '314px' }}
                    width={{ lg: '46.15%', xl: '46.43%' }}
                    maxWidth={{ lg: '314px', xl: '438px' }}
                    className={styles.sellersGalleryCol3Row2Col2}
                  >
                    {sellers['3-col']['2-row']['2-col'].map((seller) => (
                      <SellerCell key={seller.name} {...seller} />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Container>
      )}
      <ProSection isV16={isV16} />
    </Container>
  );
};

export default SellersGallery;
